import { createAsyncThunk } from '@reduxjs/toolkit';
import { isOrderComplete } from '@src/utils';
import { filter, firstValueFrom, timeout } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { NEW_ORDER_SINGLE } from '../../../../../tokens';
import {
  OrdTypeEnum,
  SecurityTypesUtils,
  SideEnum,
  sideEnumToOrderFormSide,
  TimeInForceEnum,
  type CustomerOrder,
} from '../../../../../types';
import type { SwapNewOrderSingle, WhiteLabelNewOrderSingle, WhiteLabelRfqNewOrderSingle } from '../../../OrderService';
import type { WLAppExtraArgument, WLRootState } from '../../../WLOrderFormStore';
import { setSwapFocusedOrder, setSwapSentOrderPayload, setSwapStep } from '../reducer';
import { selectSecurityAndSide } from '../selector';
import { isCryptoCryptoSwapSecurity } from '../utils';

export const sendLimitOrderThunk = createAsyncThunk<
  CustomerOrder | undefined,
  void,
  {
    state: WLRootState;
    extra: WLAppExtraArgument | undefined;
  }
>('swap/sendLimitOrderThunk', async (_arg, { extra, dispatch, rejectWithValue, getState }) => {
  const wsClient = extra?.wsClient;
  if (!wsClient) {
    return;
  }
  const state = getState();
  const quote = state.swap.quote;
  const securityAndSide = selectSecurityAndSide(state);
  if (!securityAndSide || !quote || !SecurityTypesUtils.Spot.isType(securityAndSide.security)) {
    return undefined;
  }

  const formState = state.swap.form;
  const { security, side } = securityAndSide;

  // extract the qty that is equal to the baseCurrency of the security,
  const baseCurrencyQty =
    formState.fromCurrency.value?.Symbol === security.BaseCurrency ? formState.fromQty : formState.toQty;
  const price = quote[side === SideEnum.Buy ? 'OfferPx' : 'BidPx'];

  // prepare a limit base currency order
  const ClOrdID = uuid();
  const limitOrder: WhiteLabelNewOrderSingle = {
    Symbol: security.Symbol,
    ClOrdID: ClOrdID,
    Side: sideEnumToOrderFormSide(side),
    OrderQty: baseCurrencyQty.value ?? '',
    Price: price,
    Currency: security.BaseCurrency,
    Strategy: 'Limit',
    MarketAccount: formState.marketAccount.value?.SourceAccountID,
    TransactTime: new Date().toISOString(),
    TimeInForce: TimeInForceEnum.FillOrKill,
    OrdType: OrdTypeEnum.Limit,
    Parameters: {},
  };

  // Crypto-crypto swaps (e.g. BTC-ETH) must execute the RFQ quote directly: the backend
  // requires an RFQ-linked order, with OrderQty/Currency matching the quote, rather than a
  // plain limit order. A limit order is only valid when the quote currency is a settlement
  // currency (USDT/PHP for GCash).
  const rfqOrder: WhiteLabelRfqNewOrderSingle | undefined = isCryptoCryptoSwapSecurity(security, [
    state.swap.referenceData.favoredPairCurrency?.Symbol,
    state.swap.referenceData.homeCurrency?.Symbol,
  ])
    ? {
        ClOrdID,
        Side: sideEnumToOrderFormSide(side),
        RFQID: quote.RFQID,
        Symbol: quote.Symbol ?? security.Symbol,
        Currency: quote.Currency,
        OrderQty: quote.OrderQty,
        Price: price,
        OrdType: OrdTypeEnum.LimitAllIn,
        MarketAccount: quote.MarketAccount,
      }
    : undefined;

  const order: SwapNewOrderSingle = rfqOrder ?? limitOrder;

  // Send the order
  wsClient.registerPublication({
    type: NEW_ORDER_SINGLE,
    data: [order],
  });
  dispatch(setSwapSentOrderPayload(order));

  // We need this order to be terminal, determined by the status
  const receivedOrder = await firstValueFrom(
    extra.sessionOrdersObs.pipe(
      filter(order => order.ClOrdID === ClOrdID),
      filter(order => isOrderComplete(order.OrdStatus)),
      timeout(5000)
    )
  ).catch(err => {
    return undefined;
  });
  if (!receivedOrder) {
    return rejectWithValue(
      'Did not receive an order update, please ensure a good internet connection and check your transactions'
    );
  }
  dispatch(setSwapFocusedOrder(receivedOrder));
  dispatch(setSwapStep('sentEntity'));
  return receivedOrder;
});
